import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Loader2, Sun, Moon, Palette, ChevronDown, Check } from "lucide-react";
import { splitTajweedByWord, toArabicDigits, TAJWEED_CATEGORIES, END_RUN } from "../lib/tajweed";
import { useLanguage } from "../i18n/LanguageContext";

/* ───────────────────── Madani Mushaf page renderer ─────────────────────
   Renders one page of the KFGQPC (King Fahd Complex) 15-line Madani layout.

   Data source: GET /api/quran/page/{n} → quran.com v4 word-by-word JSON with
   page/line numbers and the page-specific `code_v1` glyph code. Each Mushaf
   page uses its OWN font file (p{n}.woff2) where `code_v1` characters render
   the precise typeset glyphs. This is the authoritative approach used by
   Tarteel and quran.com itself.

   Props:
     pageNo            – integer 1..604 (the full Madani mushaf)
     statusByVerseWord – optional { "verse_key/position": "ok"|"partial"|"wrong"|"missed" }
                         for overlaying live-test highlighting on top of the page
     onWordClick       – optional (verseKey, position, status, text) callback for interactivity;
                         by default only fires for "wrong"/"missed" words (tajweed-tip use case)
     wordsClickable    – optional bool; when true, every word is clickable (not just
                         wrong/missed) — used by plain reading views like SurahView
                         where tapping any word should just navigate somewhere.
     onSwipeNext       – optional () => void; called on a right-swipe (UN1). Mushaf
                         pages turn right-to-left, so dragging the page to the RIGHT
                         moves forward, the same direction the Prev/Next buttons use.
     onSwipePrev       – optional () => void; called on a left-swipe (UN1).
     onAyahHover       – optional (verseKey | null) => void; fires as the pointer
                         moves onto/off an ayah. Used by the self-test's
                         hover-to-reveal (UM3); independent of wordsClickable so a
                         non-interactive page can still report hover.
     readerMode        – optional bool; turns on the standalone "real mushaf page"
                         reading chrome: light/dark theme toggle, a "Mushaf" script
                         picker (Hafs / Hafs Tajweed / Uthmani / Indo-Pak — see
                         MUSHAF_SCRIPTS below), and an ornamental surah-name banner
                         wherever a new surah starts on this page. Off by default so
                         the three live-test call sites in HifzTest.jsx (which use
                         color to mean correct/wrong/missed, not tajweed rules)
                         render exactly as before. Theme + script preference persist
                         in localStorage so they carry over between pages.
     onPageInfo        – optional ({surah, ayah, juz, hizb}) => void; fires
                         whenever this page's data loads, reporting its
                         first verse — lets a caller build an accurate deep
                         link (e.g. My Quran's floating Tadabbur shortcut,
                         N1) without re-fetching page data itself already has.
     onHeaderClick     – optional () => void; when set (readerMode only), the
                         "{Surah} · Page N · Juz X · Hizb Y" title block
                         renders as a button and calls this on tap. My Quran
                         uses it to show/hide its reader toolbar (Listen +
                         Surah/Verse/Page/Juz jumps), which starts collapsed.
     headerExpanded    – optional bool; that toolbar's open state, so the
                         header button can carry aria-expanded and rotate its
                         chevron.
     belowHeader       – optional node (readerMode only); rendered in the gap
                         between the header and the page body. My Quran drops
                         its collapsible reader toolbar here.
*/

// The reader's "Mushaf" dropdown — four ways to render the same word:
//   hafs          – KFGQPC Hafs v1 page-specific glyph font (code_v1), the
//                   long-standing default, no color.
//   hafs_tajweed  – the same Hafs glyphs, colorized per tajweed rule (was
//                   previously its own standalone "Tajweed colors" toggle).
//   uthmani       – plain Unicode Uthmani script (quran.com's `text_uthmani`
//                   word field), rendered with a system/web Arabic font
//                   rather than the page-specific glyph font.
//   indopak       – plain Unicode Indo-Pak script (quran.com's
//                   `text_indopak` word field) — a genuinely different
//                   orthography, not a font reskin of the Uthmani text.
//                   Rendered with the same general Arabic font stack as
//                   Uthmani, since no dedicated Indo-Pak Naskh webfont is
//                   loaded in this app yet — the text itself is correct
//                   Indo-Pak orthography, just not in a traditional
//                   Indo-Pak press-style typeface.
const MUSHAF_SCRIPTS = [
  { id: "uthmani", label: "Uthmani" },
  { id: "indopak", label: "Indo-Pak" },
  { id: "hafs", label: "Hafs" },
  { id: "hafs_tajweed", label: "Hafs Tajweed" },
];
// A real Arabic text font, standing in wherever a word ISN'T rendered via
// the page-specific code_v1 glyph font — that font is built only for its
// own PUA codepoints, but some of these page fonts also (incorrectly) claim
// coverage of normal Arabic Unicode, replacing real letters with blank
// "tofu" boxes. Forcing this stack sidesteps that regardless of page/script.
const PLAIN_ARABIC_FONT = "'Noto Naskh Arabic','Scheherazade New','Amiri Quran','Traditional Arabic',serif";

// Strips Unicode Private Use Area codepoints (U+E000–U+F8FF) — quran.com's
// `text_indopak` word field embeds a few of these for small stop/pause
// marks that only render as real glyphs in their own paired Indo-Pak press
// font. With no such font loaded here (see MUSHAF_SCRIPTS comment above),
// these render as broken boxes in every other font by definition — a PUA
// codepoint has no meaning to a font that didn't specifically define one.
// Omitting them is the honest fallback until a licensed font is sourced.
const PUA_RE = /[\uE000-\uF8FF]/g;
function stripPua(s) {
  return s ? s.replace(PUA_RE, "") : s;
}

// Lightweight in-memory cache so toggling between pages doesn't refetch.
const _pageCache = new Map();
const _fontsLoaded = new Set();
let _surahNamesPromise = null;

function loadPageFont(pageNo) {
  if (_fontsLoaded.has(pageNo)) return;
  _fontsLoaded.add(pageNo);
  const style = document.createElement("style");
  style.setAttribute("data-mushaf-page", String(pageNo));
  // KFGQPC Hafs v1 page-specific font — free, hosted via jsdelivr's mirror of
  // quran.com's open-source frontend (CORS-safe + cached globally). Falling
  // back to common Arabic system fonts if the CDN is unreachable so the page
  // still renders (just without the calligraphic Madani fidelity).
  const url = `https://cdn.jsdelivr.net/gh/quran/quran.com-frontend-next@master/public/fonts/quran/hafs/v1/woff2/p${pageNo}.woff2`;
  style.textContent = `@font-face{font-family:'p${pageNo}'; src:url('${url}') format('woff2'); font-display:swap;}`;
  document.head.appendChild(style);
}

function getSurahNames() {
  if (!_surahNamesPromise) {
    _surahNamesPromise = api.get("/quran/surahs")
      .then((r) => Object.fromEntries((r.data || []).map((s) => [s.number, s])))
      .catch(() => ({}));
  }
  return _surahNamesPromise;
}

const STATUS_CLS = {
  ok: "text-emerald-700",
  partial: "text-amber-700 underline decoration-dotted decoration-amber-500",
  wrong: "text-terracotta-600 bg-terracotta-500/15 rounded px-0.5 cursor-pointer hover:bg-terracotta-500/25",
  missed: "text-foreground/40 line-through cursor-pointer hover:text-terracotta-600",
};

function readPref(key, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

export default function MadaniPage({ pageNo, statusByVerseWord, hiddenAyahs, onWordClick, wordsClickable, readerMode, onSwipeNext, onSwipePrev, onAyahHover, onPageInfo, onHeaderClick, headerExpanded, belowHeader }) {
  const { tx, t: tr } = useLanguage();
  const [page, setPage] = useState(() => _pageCache.get(pageNo) || null);
  const [loading, setLoading] = useState(!_pageCache.has(pageNo));
  const [surahNames, setSurahNames] = useState({});
  const [theme, setTheme] = useState(() => readPref("mushaf-theme", "light"));
  const [mushafScript, setMushafScript] = useState(() => {
    const saved = readPref("mushaf-script", "hafs");
    return MUSHAF_SCRIPTS.some((s) => s.id === saved) ? saved : "hafs";
  });
  const [scriptMenuOpen, setScriptMenuOpen] = useState(false);
  // UC8 — the whole ayah is the tap target, so hover highlights the whole ayah.
  const [hoverVerse, setHoverVerse] = useState(null);
  const ref = useRef(null);
  const scriptMenuRef = useRef(null);
  const touchRef = useRef(null); // UN1 — swipe start point
  const bodyRef = useRef(null);  // wraps the 15 mushaf lines — see the fit-to-width effect

  useEffect(() => {
    if (!scriptMenuOpen) return;
    const onDown = (e) => { if (scriptMenuRef.current && !scriptMenuRef.current.contains(e.target)) setScriptMenuOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setScriptMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [scriptMenuOpen]);

  useEffect(() => { loadPageFont(pageNo); }, [pageNo]);

  useEffect(() => {
    if (!readerMode) return;
    getSurahNames().then(setSurahNames);
  }, [readerMode]);

  useEffect(() => {
    if (!readerMode) return;
    try { window.localStorage.setItem("mushaf-theme", theme); } catch { /* ignore */ }
  }, [readerMode, theme]);

  useEffect(() => {
    if (!readerMode) return;
    try { window.localStorage.setItem("mushaf-script", mushafScript); } catch { /* ignore */ }
  }, [readerMode, mushafScript]);

  // Leaving the page clears any ayah hover state so the next page starts clean.
  useEffect(() => { setHoverVerse(null); }, [pageNo]);

  useEffect(() => {
    if (_pageCache.has(pageNo)) {
      setPage(_pageCache.get(pageNo));
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/quran/page/${pageNo}`)
      .then((r) => { _pageCache.set(pageNo, r.data); setPage(r.data); })
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [pageNo]);

  // N1 — reports this page's first verse (surah/ayah/juz/hizb) up to
  // whatever's rendering the page, so a caller can build an accurate
  // deep link (e.g. the reading view's floating Tadabbur shortcut) without
  // duplicating its own fetch of data this component already has.
  useEffect(() => {
    if (!page) return;
    const v = page.verses[0];
    if (v) onPageInfo?.({ surah: v.surah, ayah: v.ayah, juz: v.juz, hizb: v.hizb });
  }, [page, onPageInfo]);

  // Group words by line number (1..15). Each entry: { line: n, words: [{...verseKey,...}]}
  const lines = useMemo(() => {
    if (!page) return [];
    const byLine = new Map();
    page.verses.forEach((v) => {
      v.words.forEach((w) => {
        const key = `${v.key}/${w.position}`;
        const enriched = { ...w, verseKey: v.key, ayah: v.ayah, surah: v.surah, _key: key };
        if (!byLine.has(w.line)) byLine.set(w.line, []);
        byLine.get(w.line).push(enriched);
      });
    });
    return Array.from(byLine.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([line, words]) => ({ line, words }));
  }, [page]);

  // Which line does each new surah's opening ayah first appear on? (readerMode banner)
  const surahBannerByLine = useMemo(() => {
    if (!readerMode || !page) return {};
    const out = {};
    page.verses.forEach((v) => {
      if (v.ayah !== 1) return;
      const firstWordLine = v.words[0]?.line;
      if (firstWordLine != null) out[firstWordLine] = v.surah;
    });
    return out;
  }, [readerMode, page]);

  // Per-word tajweed color runs, keyed the same way as `lines` above
  // (`${verseKey}/${position}`) — so tajweed mode colors words *in place*
  // within the exact same per-line grid the glyph rendering uses, rather
  // than reflowing the page into a separate flowing paragraph. (An earlier
  // version did the latter; besides losing the authentic per-page line
  // breaks, concatenating several ayahs that each end in a number into one
  // HTML string confused the browser's bidi algorithm into rendering the
  // ayahs out of order — e.g. 2,1,4,3,6,5,7 instead of 1-7.) A verse whose
  // word count doesn't line up with its tajweed-word split (should only
  // happen if the two ever disagree on tokenization) is skipped, falling
  // back to plain uncolored text for just that verse rather than risking a
  // misaligned/garbled render.
  const tajweedWordColors = useMemo(() => {
    if (!readerMode || !page) return {};
    const map = {};
    page.verses.forEach((v) => {
      if (!v.text_tajweed) return;
      const chunks = splitTajweedByWord(v.text_tajweed);
      if (chunks.length !== v.words.length) return;
      v.words.forEach((w, i) => { map[`${v.key}/${w.position}`] = chunks[i]; });
    });
    return map;
  }, [readerMode, page]);

  // Fit-to-width: the KFGQPC page font sizes each line to one justified row,
  // but only at its own designed line width — inside this app's narrow frame
  // a dense line (a typical Al-Baqarah line) overflows and wraps onto a
  // second row, which is exactly what breaks the page's alignment. So after
  // layout we measure every line and scale down any that overflows
  // (transform only — the line's layout box keeps its full size, so the
  // vertical rhythm between lines stays even). Re-runs on page/script change,
  // once the page-specific webfont has loaded, and on container resize.
  useLayoutEffect(() => {
    const container = bodyRef.current;
    if (!container) return undefined;
    let raf = 0;
    let cancelled = false;
    const fit = () => {
      container.querySelectorAll("[data-fit-line]").forEach((el) => {
        el.style.transform = "none";
        const avail = el.parentElement ? el.parentElement.clientWidth : 0;
        const full = el.scrollWidth;
        const scale = full > avail && full > 0 ? (avail / full) * 0.995 : 1;
        el.style.transform = scale < 1 ? `scale(${scale})` : "none";
      });
    };
    const schedule = () => {
      if (cancelled) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    };
    schedule();
    // p{n}.woff2 loads async — the first measure runs in the fallback font,
    // so remeasure once the real glyph font is in.
    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.load(`1em 'p${pageNo}'`).then(schedule).catch(() => {});
      document.fonts.ready.then(schedule).catch(() => {});
    }
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(schedule);
      ro.observe(container);
    }
    window.addEventListener("resize", schedule);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [pageNo, mushafScript, page, theme, readerMode]);

  // UN1 — swipe to turn pages, so it feels like a real mushaf. Direction is
  // mirrored for RTL: dragging right moves FORWARD (the next page sits to the
  // left), matching the mirrored Prev/Next buttons. A swipe must be mostly
  // horizontal and long enough that it isn't just a sloppy tap or a scroll.
  const SWIPE_MIN = 60;
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    touchRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx > 0) onSwipeNext?.();
    else onSwipePrev?.();
  };

  const dark = readerMode && theme === "dark";

  if (loading) {
    return (
      <div className="hifz-card flex items-center justify-center min-h-[400px]" data-testid="madani-loading">
        <Loader2 className="h-6 w-6 animate-spin text-forest-700" />
      </div>
    );
  }
  if (!page) {
    return (
      <div className="hifz-card text-center text-sm text-muted-foreground" data-testid="madani-error">
        {tr("madaniPage.madaniPageNotAvailableRight", { pageNo })}
      </div>
    );
  }

  // readerMode drops the boxed-card look entirely — no border, no shadow —
  // so the content reads as a plain book page rather than a card sitting on
  // the page. Non-readerMode (the 3 HifzTest call sites) keeps the original
  // bordered card so nothing there visually shifts.
  const pageStyle = { fontFamily: `'p${pageNo}', 'Amiri Quran', 'Scheherazade New', serif` };
  if (readerMode) pageStyle.backgroundColor = dark ? "#141210" : "#F9F6F0";
  else pageStyle.backgroundColor = dark ? "#141210" : "#fdf6e3";

  const firstVerse = page.verses[0];
  // Hafs (plain, no tajweed) is the only mode that renders the page-specific
  // KFGQPC code_v1 glyph font. That font is real Mushaf typesetting: every
  // line, regardless of how many words it holds, is drawn (via kashida/
  // letter-stretching) to fill the SAME full line width -- that is the
  // entire point of justified Mushaf printing. So a dense Baqarah line and a
  // sparse Fatihah line should render at approximately the same total width
  // for a given font-size, by construction -- confirming Al-Fatihah at
  // text-3xl is meaningfully more trustworthy evidence for Hafs than it is
  // for the other three scripts (below).
  //
  // Uthmani, Indo-Pak, and Hafs Tajweed all fall back to a generic Arabic
  // text stack instead (see PLAIN_ARABIC_FONT above) -- ordinary flowing
  // text with NO per-line width normalization, so a line's rendered width
  // scales directly with how many words/letters it actually has. This is
  // confirmed broken, not just theoretical: at text-2xl, Hafs Tajweed
  // wrapped mid-ayah on Al-Baqarah page 3 (dropping the line's last word to
  // its own row) -- a line Al-Fatihah simply never has an equivalent of.
  // Dropped a full tier to text-xl for some real margin, since this exact
  // failure mode has now recurred twice at two different sizes.
  const lineTextSizeCls = mushafScript === "hafs" ? "text-3xl" : "text-xl";
  const headerSurah = surahNames[firstVerse?.surah];

  return (
    <div
      ref={ref}
      className={`mushaf-page rounded-xl p-4 transition-colors duration-300 ${
        readerMode ? "" : `shadow-lg border ${dark ? "border-gold-500/25" : "border-gold-500/40"}`
      }`}
      data-testid={`madani-page-${pageNo}`}
      style={pageStyle}
      onTouchStart={onSwipeNext || onSwipePrev ? onTouchStart : undefined}
      onTouchEnd={onSwipeNext || onSwipePrev ? onTouchEnd : undefined}
    >
      {/* Header: (readerMode) a plain title/subtitle bar — "{Surah} / Page N ·
          Juz X · Hizb Y" — like a real Mushaf app's top bar, plus icon-only
          theme + tajweed toggles. Non-readerMode keeps the original plain
          "── Page N ──" label so the 3 HifzTest call sites don't shift. */}
      {readerMode ? (
        <div className={`flex items-start justify-between gap-3 mb-5 pb-3 border-b font-sans ${dark ? "border-white/10" : "border-black/10"}`} data-testid="mushaf-reader-header">
          {onHeaderClick ? (
            /* The title block doubles as the show/hide control for the
               reader's toolbar (Listen + Surah/Verse/Page/Juz jumps) — that
               lives in the parent and starts collapsed — so it gets a
               bordered pill, a hover state and a chevron to read as
               tappable rather than as a plain label. */
            <button
              type="button"
              onClick={onHeaderClick}
              aria-expanded={!!headerExpanded}
              className={`flex items-center gap-2 -my-1 -ml-1.5 pl-2 pr-2.5 py-1 rounded-lg border text-left transition ${
                dark ? "border-white/20 hover:bg-white/5" : "border-black/15 hover:bg-black/5"
              }`}
              data-testid="mushaf-reader-header-toggle"
              title={headerExpanded ? tr("madaniPage.hideReaderControls") : tr("madaniPage.showReaderControls")}
            >
              <span className="min-w-0">
                <span className={`block font-display font-semibold text-base ${dark ? "text-sand-100" : "text-foreground/90"}`}>
                  {headerSurah?.name_en || (firstVerse ? tr("madaniPage.surah", { surah: firstVerse.surah }) : "")}
                </span>
                <span className={`block text-xs mt-0.5 ${dark ? "text-sand-100/50" : "text-foreground/50"}`} data-testid="mushaf-reader-subtitle">
                  {[tr("common.pageN", { n: pageNo }), firstVerse?.juz != null && tr("common.juzN", { n: firstVerse.juz }), firstVerse?.hizb != null && tr("common.hizbN", { n: firstVerse.hizb })].filter(Boolean).join(" · ")}
                </span>
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${headerExpanded ? "rotate-180" : ""} ${dark ? "text-sand-100/60" : "text-foreground/45"}`} />
            </button>
          ) : (
            <div>
              <div className={`font-display font-semibold text-base ${dark ? "text-sand-100" : "text-foreground/90"}`}>
                {headerSurah?.name_en || (firstVerse ? tr("madaniPage.surah", { surah: firstVerse.surah }) : "")}
              </div>
              <div className={`text-xs mt-0.5 ${dark ? "text-sand-100/50" : "text-foreground/50"}`} data-testid="mushaf-reader-subtitle">
                {[tr("common.pageN", { n: pageNo }), firstVerse?.juz != null && tr("common.juzN", { n: firstVerse.juz }), firstVerse?.hizb != null && tr("common.hizbN", { n: firstVerse.hizb })].filter(Boolean).join(" · ")}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1.5 shrink-0" data-testid="mushaf-reader-toolbar">
            <div className="relative" ref={scriptMenuRef}>
              <button
                type="button"
                onClick={() => setScriptMenuOpen((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition ${
                  mushafScript !== "hafs"
                    ? `bg-gold-500/20 border-gold-500/50 ${dark ? "text-gold-400" : "text-gold-600"}`
                    : dark
                      ? "border-white/15 text-sand-100/70 hover:bg-white/5"
                      : "border-black/10 text-foreground/60 hover:bg-black/5"
                }`}
                data-testid="mushaf-script-toggle"
                title={tr("madaniPage.chooseTheMushafScript")}
                aria-haspopup="listbox"
                aria-expanded={scriptMenuOpen}
              >
                <Palette className="h-3.5 w-3.5" /> {tr("madaniPage.mushaf")} <ChevronDown className="h-3 w-3" />
              </button>
              {scriptMenuOpen && (
                <div
                  role="listbox"
                  className={`absolute right-0 mt-1.5 w-40 rounded-xl border shadow-xl z-30 overflow-hidden font-sans ${
                    dark ? "bg-[#1c1a17] border-white/15" : "bg-white border-sand-300"
                  }`}
                  data-testid="mushaf-script-menu"
                >
                  {MUSHAF_SCRIPTS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      role="option"
                      aria-selected={mushafScript === s.id}
                      onClick={() => { setMushafScript(s.id); setScriptMenuOpen(false); }}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[11px] text-left transition ${
                        dark ? "text-sand-100/80 hover:bg-white/5" : "text-foreground/75 hover:bg-sand-100"
                      }`}
                      data-testid={`mushaf-script-option-${s.id}`}
                    >
                      {tx(s.label)}
                      {mushafScript === s.id && <Check className="h-3.5 w-3.5 text-gold-600 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
              className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border transition ${
                dark ? "border-white/15 text-sand-100/80 hover:bg-white/5" : "border-black/10 text-foreground/60 hover:bg-black/5"
              }`}
              data-testid="mushaf-theme-toggle"
              title={dark ? tr("madaniPage.switchToLightPage") : tr("madaniPage.switchToDarkPage")}
              aria-label={tr("madaniPage.togglePageTheme")}
            >
              {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      ) : (
        <div className={`text-center text-[10px] uppercase tracking-[0.18em] font-sans font-semibold mb-4 ${dark ? "text-gold-400/80" : "text-gold-700/80"}`} data-testid="madani-page-no">
          {tr("madaniPage.page", { pageNo })}
        </div>
      )}

      {/* Slot the reader (My Quran) uses to drop its toolbar in right below
          the header — the header button toggles it. font-sans + dir=ltr +
          an explicit fontFamily are all needed: this sits inside the page
          <div>, which forces the per-page KFGQPC glyph font and (via the
          body) RTL — without the reset the pill labels ("1 · Al-Faatihah",
          "Page 1"…) render as Mushaf glyphs / Arabic-Indic digits. */}
      {readerMode && belowHeader && (
        <div
          className="mb-5 -mx-2 font-sans"
          dir="ltr"
          style={{ fontFamily: "'Work Sans', ui-sans-serif, system-ui, sans-serif" }}
          data-testid="madani-below-header"
        >
          {belowHeader}
        </div>
      )}

      {mushafScript === "hafs_tajweed" && readerMode && (
        <div className={`flex flex-wrap justify-center gap-x-3 gap-y-1 mb-6 font-sans text-[10px] ${dark ? "text-sand-100/60" : "text-foreground/55"}`} data-testid="tajweed-legend">
          {Object.entries(TAJWEED_CATEGORIES).map(([key, cat]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1"
              title={cat.inferred ? tr("madaniPage.inferredFromTheArabicText", { description: cat.description }) : cat.description}
            >
              <span className="h-2 w-2 rounded-full inline-block" style={{ background: cat.color }} />
              {tx(cat.label)}
              {cat.inferred && <sup className="opacity-70">*</sup>}
            </span>
          ))}
        </div>
      )}

      {/* Body: the same 15-line-grid structure regardless of tajweed mode —
          only what's rendered inside each word changes. Tajweed mode can't
          reuse the code_v1 glyph (a per-word font glyph has no per-letter
          color data), so it renders each word's plain Uthmani text split
          into colored runs instead; the line breaks, surah banner, and
          click/status/hidden handling are exactly the glyph path's, so
          toggling tajweed colors never changes the page's structure. */}
      <div className="space-y-4" dir="rtl" ref={bodyRef}>
        {lines.map(({ line, words }) => (
          <React.Fragment key={line}>
            {readerMode && surahBannerByLine[line] && (
              <SurahBanner name={surahNames[surahBannerByLine[line]]?.name_ar} dark={dark} />
            )}
            {/* C4 — tried edge-to-edge justify-between here to mimic real
                Mushaf typesetting, but with only 3-5 words on most lines it
                stretched the gaps far wider than a real printed page ever
                does (that gets its even look from many more words per line
                plus kashida/letter-stretching, not raw word-gap spacing).
                Reverted to centered per direct visual confirmation — see
                thread.

                Font size is lineTextSizeCls (computed above, per
                mushafScript — see that comment for why Hafs and the other
                three scripts need different sizes), no md: escalation —
                the original text-3xl md:text-4xl + tracking-wide + wider
                padding is what broke inside the always-narrow phone frame
                (see AppLayout.jsx), since md: still fires on any real
                laptop-width window regardless of the frame's fixed narrow
                width. Both tiers are confirmed only on Al-Fatihah's page 1,
                whose layout is unusually spacious (a decorative first-page
                treatment, fewer words per line) — a typical denser
                Baqarah-range line hasn't been confirmed at either size and
                may need nudging. Update: instead of hand-tuning a size per
                page, the fit-to-width effect above measures each line after
                layout and scales down only the ones that would still
                overflow the frame — so every line stays a single row on
                every page and every script, while roomy pages keep the
                larger size. Hence flex-nowrap + whitespace-nowrap here (no
                more flex-wrap): a line overflows rather than wrapping, then
                gets scaled to fit. */}
            <div className="flex justify-center overflow-hidden" data-testid={`madani-line-${line}`}>
              <div
                data-fit-line="1"
                className={`flex shrink-0 flex-nowrap items-baseline justify-center gap-x-1 whitespace-nowrap ${lineTextSizeCls} leading-[2] select-none will-change-transform`}
              >
              {words.map((w) => {
                const status = statusByVerseWord?.[w._key];
                const isEnd = w.char_type === "end";
                const isHidden = !!hiddenAyahs?.has?.(w.verseKey) && !status;
                const clickable = !!onWordClick && (wordsClickable || status === "wrong" || status === "missed");
                // UC8 — in reading views the tap target is the whole ayah, so
                // hovering any word highlights every word of that ayah.
                const ayahHover = wordsClickable && hoverVerse === w.verseKey && !isHidden;
                const tajChunk = mushafScript === "hafs_tajweed" && !isHidden && !status ? tajweedWordColors[w._key] : null;
                // Uthmani/Indo-Pak render real Unicode text instead of the
                // page-specific code_v1 glyph — see MUSHAF_SCRIPTS above.
                // `text_indopak` embeds Private Use Area codepoints (e.g.
                // U+E021) for a few small stop/pause marks — quran.com's
                // own Indo-Pak press font maps those PUA codepoints to real
                // glyphs, but by definition a PUA codepoint has no meaning
                // to any *other* font, including our generic Arabic fallback
                // stack — so with no dedicated Indo-Pak font (the same
                // licensing gap already flagged), those specific characters
                // can only ever render as broken boxes, never a real glyph
                // in a different font. Stripping them out is the honest fix
                // available today: silently omitting one small decorative
                // mark reads far better than a visible glitch. Confirmed via
                // a direct fetch of quran.com's own API that this is present
                // upstream, not something our pipeline corrupts.
                const plainScriptText = mushafScript === "uthmani" ? w.text
                  : mushafScript === "indopak" ? stripPua(w.text_indopak) || w.text
                  : null;
                // Both tajweed runs and plain Uthmani/Indo-Pak text need the
                // real-font override (dodging the page font's tofu-box bug).
                const needsPlainFont = !!tajChunk || plainScriptText != null;
                let cls;
                if (isHidden) {
                  // Selected ayahs start fully hidden; reveal happens once a status lands.
                  cls = "opacity-0 select-none pointer-events-none";
                } else if (status) {
                  cls = STATUS_CLS[status];
                } else if (needsPlainFont) {
                  // Base color for the word's non-ruled letters (each ruled
                  // run overrides this via its own inline color below) —
                  // this must be a real visible color, not "inherit" with
                  // nothing set, or plain letters vanish against the page
                  // background (the bug: they rendered near-invisible).
                  cls = dark ? "text-sand-100" : "text-foreground/90";
                } else if (readerMode && !dark) {
                  // Plain ink-black in the light reader theme, matching
                  // how a real printed mushaf page reads (no green/gold
                  // tinting) — HifzTest's non-readerMode calls keep the
                  // brand forest/gold coloring below, untouched.
                  cls = "text-foreground/90";
                } else {
                  cls = isEnd
                    ? (dark ? "text-gold-400" : "text-gold-700")
                    : (dark ? "text-sand-100" : "text-forest-800");
                }
                return (
                  <span
                    key={w._key}
                    className={`inline-block transition ${cls} ${
                      clickable ? "cursor-pointer" : "cursor-default"
                    } ${clickable && !wordsClickable ? "hover:text-gold-600" : ""} ${
                      ayahHover ? (dark ? "bg-gold-400/20 rounded-sm" : "bg-gold-500/20 rounded-sm") : ""
                    }`}
                    data-testid={`madani-word-${w._key}`}
                    data-verse-key={w.verseKey}
                    data-position={w.position}
                    data-status={status || ""}
                    onMouseEnter={wordsClickable || onAyahHover ? () => {
                      if (wordsClickable) setHoverVerse(w.verseKey);
                      onAyahHover?.(w.verseKey);
                    } : undefined}
                    onMouseLeave={wordsClickable || onAyahHover ? () => {
                      if (wordsClickable) setHoverVerse((v) => (v === w.verseKey ? null : v));
                      onAyahHover?.(null);
                    } : undefined}
                    onClick={() => { if (clickable) onWordClick(w.verseKey, w.position, status, w.text); }}
                    title={clickable ? (wordsClickable ? tr("madaniPage.tapToOpenThisAyah") : tr("madaniPage.tapForTajweedTip")) : tr("madaniPage.word", { verseKey: w.verseKey, position: w.position })}
                    style={needsPlainFont ? { fontFamily: PLAIN_ARABIC_FONT } : undefined}
                  >
                    {tajChunk
                      ? tajChunk.map((run, i) =>
                          run.color === END_RUN ? (
                            <span
                              key={i}
                              className={`inline-flex items-center justify-center h-[1.6em] min-w-[1.6em] mx-0.5 rounded-full border align-middle text-[0.55em] ${
                                dark ? "border-gold-400 text-gold-400" : "border-current text-current"
                              }`}
                            >
                              {tx(run.text)}
                            </span>
                          ) : (
                            <span key={i} style={{ color: run.color || undefined }}>
                              {tx(run.text)}
                            </span>
                          )
                        )
                      : plainScriptText != null
                        ? (isEnd
                            ? (
                              <span
                                className={`inline-flex items-center justify-center h-[1.6em] min-w-[1.6em] mx-0.5 rounded-full border align-middle text-[0.55em] ${
                                  dark ? "border-gold-400 text-gold-400" : "border-current text-current"
                                }`}
                              >
                                {plainScriptText}
                              </span>
                            )
                            : plainScriptText)
                        : (w.code_v1 || w.text)}
                  </span>
                );
              })}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {readerMode && (
        <div className={`text-center mt-8 text-sm font-sans ${dark ? "text-gold-400/50" : "text-foreground/40"}`} data-testid="mushaf-page-footer">
          {toArabicDigits(pageNo)}
        </div>
      )}
    </div>
  );
}

/* The surah-header plaque, composed 3-slice style from the real artwork:

     [cap image] [ stretchable middle + surah name ] [cap image, mirrored]

   `/surah-banner-cap.png` is the ornament panel + roundel + the rounded end
   of the name cartouche; `/surah-banner-mid.png` is a 10px slice of the
   cartouche's uniform run, stretched to whatever width is left, so the
   plaque fits any surah name without distorting the ornament. Both were cut
   from the reference image the user supplied ("design for hwh") and levelled
   to clean up its JPEG noise.

   The source art is white line-work on black. In the dark reader theme it
   composites with `screen` (black drops out); in the light theme it's
   inverted first and composites with `multiply` (white drops out) — so in
   both cases only the line-work paints and the page colour shows through,
   with no plaque-coloured rectangle edge. */
function SurahBanner({ name, dark }) {
  if (!name) return null;
  const blend = dark
    ? { mixBlendMode: "screen" }
    : { filter: "invert(1)", mixBlendMode: "multiply" };
  return (
    <div className="flex justify-center my-4" data-testid="mushaf-surah-banner">
      <div className="flex items-stretch w-full max-w-2xl" style={blend}>
        <img src="/surah-banner-cap.png" alt="" className="w-[30%] h-auto shrink-0" />
        <div
          className="relative flex-1 min-w-0"
          style={{
            backgroundImage: 'url("/surah-banner-mid.png")',
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
          }}
        >
          <span className="absolute inset-0 flex items-center justify-center px-2">
            <span className="ayah-arabic text-white text-base sm:text-xl md:text-2xl leading-none whitespace-nowrap">
              سورة {name}
            </span>
          </span>
        </div>
        <img src="/surah-banner-cap.png" alt="" className="w-[30%] h-auto shrink-0 scale-x-[-1]" />
      </div>
    </div>
  );
}
